import React, { useCallback, useMemo, useState } from 'react';
import useSettings from './hooks/useSettings';

const HUNGER_CATEGORY = 'Gameplay_Hunger';
const HUNGER_FIELDS = Object.freeze([
  {
    key: 'FullToEmptyMinutes',
    label: 'Full to empty',
    help: 'Minutes at the normal 1× rate before 100% reaches 0%.',
    defaultValue: '30',
    min: 1,
    max: 1440,
    step: 1,
    unit: 'minutes',
  },
  {
    key: 'IdleDrainMultiplier',
    label: 'Idle drain',
    help: 'Satiety drain rate while Idle outside Camp. Set 0 to pause drain.',
    defaultValue: '0.25',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'RestingDrainMultiplier',
    label: 'Rest drain',
    help: 'Satiety drain rate while Resting outside Camp. Set 0 to pause drain.',
    defaultValue: '0.25',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'MovingDrainMultiplier',
    label: 'Move drain',
    help: 'Satiety drain rate while moving outside Camp.',
    defaultValue: '1',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'CollectingDrainMultiplier',
    label: 'Gathering drain',
    help: 'Satiety drain rate while gathering a resource outside Camp.',
    defaultValue: '1.5',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'ScavengingDrainMultiplier',
    label: 'Scavenge drain',
    help: 'Satiety drain rate while automatically scavenging outside Camp.',
    defaultValue: '1.5',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'FightingDrainMultiplier',
    label: 'Fight drain',
    help: 'Satiety drain rate while fighting outside Camp.',
    defaultValue: '1.5',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'FleeingDrainMultiplier',
    label: 'Flee drain',
    help: 'Satiety drain rate while fleeing outside Camp.',
    defaultValue: '1.5',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'WellFedThresholdPercent',
    label: 'Well-fed threshold',
    help: 'At or above this percentage the regeneration bonus applies.',
    defaultValue: '75',
    min: 1,
    max: 100,
    step: 1,
    unit: '%',
  },
  {
    key: 'HungryThresholdPercent',
    label: 'Hungry threshold',
    help: 'Below this percentage the hungry penalties apply.',
    defaultValue: '25',
    min: 0,
    max: 99,
    step: 1,
    unit: '%',
  },
  {
    key: 'WellFedRegenMultiplier',
    label: 'Well-fed regeneration',
    help: 'Multiplier for final HP regeneration while well fed.',
    defaultValue: '1.25',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'HungryRegenMultiplier',
    label: 'Hungry regeneration',
    help: 'Multiplier for final HP regeneration while hungry.',
    defaultValue: '0.75',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'StarvingRegenMultiplier',
    label: 'Starving regeneration',
    help: 'Multiplier for final HP regeneration at 0%.',
    defaultValue: '0.5',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'HungryDamageMultiplier',
    label: 'Hungry damage',
    help: 'Outgoing player-damage multiplier while hungry.',
    defaultValue: '0.9',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'StarvingDamageMultiplier',
    label: 'Starving damage',
    help: 'Outgoing player-damage multiplier at 0%.',
    defaultValue: '0.75',
    min: 0,
    max: 10,
    step: 0.05,
    unit: '×',
  },
  {
    key: 'StarvationDamagePercentPerMinute',
    label: 'Starvation damage',
    help: 'Maximum-HP damage per active minute, stopping at 1 HP.',
    defaultValue: '5',
    min: 0,
    max: 100,
    step: 0.5,
    unit: '% / minute',
  },
]);

export default function AdminSettings() {
  const {
    settings,
    groupedSettings,
    loading,
    error,
    mutating,
    updateSetting,
    deleteSetting,
    createSetting,
  } = useSettings();

  const [newCategory, setNewCategory] = useState('');
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const categories = useMemo(
    () => Object.keys(groupedSettings || {})
      .filter((category) => (
        category !== 'General' && category !== HUNGER_CATEGORY
      ))
      .sort((a, b) => a.localeCompare(b)),
    [groupedSettings]
  );

  const registrationSetting = settings.find(
    (setting) => setting.category === 'General' && setting.key === 'RegistrationOpen'
  );
  const registrationOpen = registrationSetting?.value?.toLowerCase() !== 'false';

  const handleRegistrationChange = useCallback(
    async (event) => {
      const value = String(event.target.checked);
      if (registrationSetting) {
        await updateSetting('General', 'RegistrationOpen', value);
      } else {
        await createSetting('General', 'RegistrationOpen', value);
      }
    },
    [createSetting, registrationSetting, updateSetting]
  );

  const handleAdd = useCallback(async () => {
    if (!newCategory || !newKey) return;
    const created = await createSetting(newCategory, newKey, newValue);
    if (created) {
      setNewCategory('');
      setNewKey('');
      setNewValue('');
    }
  }, [createSetting, newCategory, newKey, newValue]);

  const handleValueChange = useCallback(
    (category, key) => (event) => {
      updateSetting(category, key, event.target.value);
    },
    [updateSetting]
  );

  const handleHungerChange = useCallback(
    (key) => async (event) => {
      const value = event.target.value;
      const existing = settings.some(setting => (
        setting.category === HUNGER_CATEGORY && setting.key === key
      ));
      if (existing) {
        await updateSetting(HUNGER_CATEGORY, key, value);
      } else {
        await createSetting(HUNGER_CATEGORY, key, value);
      }
    },
    [createSetting, settings, updateSetting]
  );

  const handleDelete = useCallback(
    (category, key) => async () => {
      await deleteSetting(category, key);
    },
    [deleteSetting]
  );

  return (
    <div className='admin-settingspage-single'>
      <section className='general-settings-card' aria-labelledby='registration-heading'>
        <div>
          <h3 id='registration-heading'>User registration</h3>
          <p>Allow visitors to create new accounts.</p>
        </div>
        <label className='settings-toggle'>
          <input
            type='checkbox'
            checked={registrationOpen}
            onChange={handleRegistrationChange}
            disabled={loading || mutating}
          />
          <span>{registrationOpen ? 'Open' : 'Closed'}</span>
        </label>
      </section>
      <section
        className='general-settings-card general-settings-card--stacked'
        aria-labelledby='hunger-settings-heading'
      >
        <div>
          <h3 id='hunger-settings-heading'>Hunger &amp; survival</h3>
          <p>Configure action drain rates, thresholds, and hunger-state effects.</p>
        </div>
        <div className='hunger-settings-grid'>
          {HUNGER_FIELDS.map((field) => {
            const setting = settings.find(candidate => (
              candidate.category === HUNGER_CATEGORY
              && candidate.key === field.key
            ));
            return (
              <label className='hunger-settings-field' key={field.key}>
                <span>{field.label}</span>
                <span className='hunger-settings-input'>
                  <input
                    type='number'
                    disabled={loading || mutating}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    value={setting?.value ?? field.defaultValue}
                    onChange={handleHungerChange(field.key)}
                  />
                  <span>{field.unit}</span>
                </span>
                <small>{field.help}</small>
              </label>
            );
          })}
        </div>
      </section>
      <h2>Advanced settings</h2>
      <div className='admin-settings__new-row'>
        <input
          placeholder='Category'
          value={newCategory}
          onChange={(event) => setNewCategory(event.target.value)}
        />
        <input placeholder='Key' value={newKey} onChange={(event) => setNewKey(event.target.value)} />
        <input
          placeholder='Value'
          value={newValue}
          onChange={(event) => setNewValue(event.target.value)}
        />
        <button className='button-primary' onClick={handleAdd} disabled={mutating}>
          Add
        </button>
      </div>
      {loading && <div>Loading settings...</div>}
      {error && (
        <div className='error'>
          Failed to load settings{error.message ? `: ${error.message}` : '.'}
        </div>
      )}
      {categories.map((category) => (
        <section key={category} className='admin-settings__category'>
          <h3>{category}</h3>
          {(groupedSettings[category] || []).map((row) => (
            <div key={row.key} className='admin-settings__row'>
              <div className='admin-settings__key'>{row.key}</div>
              <input
                value={row.value ?? ''}
                onChange={handleValueChange(category, row.key)}
              />
              <button onClick={handleDelete(category, row.key)} disabled={mutating}>
                Delete
              </button>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
